<template>
  <div class="group-edit-mode" :class="domain.shared"
       v-if="journeyOptions">
    <div class="edit-header">
      <i class="icon icon--group"></i>
      {{ $t('group.groupSetting') }}

      <div class="group-dropdown"
           id="groupDropdown">
        <div class="selected-group"
             :class="{ disabled: group && group.usableStatus === 'PENDING' }"
             @click="openGroupList">
          <template v-if="group">
            {{ group.groupName === 'AUTO' ? $t('group.automatic') : group.groupName }}
            <ui-loading-circle v-if="group.usableStatus === 'PENDING'"
                               class="pending"
                               :isShowDescText="false"
                               :svgWidth="18" />
          </template>
          <i class="icon icon--drop-down"></i>
        </div>
        <div class="group-list"
             :class="{ open: openGroup }"
             @mouseenter="resetGroupDropdownTimer"
             @mouseleave="startGroupDropdownTimer">
          <ul>
            <li v-for="item in groupItems"
                :key="`group${item.value.groupId}`"
                @mouseleave="menuOpen = -1">
              <div v-if="!editing || editing.groupId !== item.value.groupId"
                   :class="{ disabled: item.value.usableStatus === 'PENDING' }"
                   @click="onGroupChange(item.value)">
                {{ item.value.groupName === 'AUTO' ? $t('group.automatic') : item.value.groupName }}

                <ui-loading-circle v-if="item.value.usableStatus === 'PENDING'"
                                   class="pending"
                                   :isShowDescText="false"
                                   :svgWidth="18" />

                <span class="tag-menu">
                  <span class="opener"
                        @click.stop="openMenu(item.value.groupId)">
                    <i class="icon icon--hamburger"></i>
                  </span>
                  <span class="menu-ul"
                        :class="{ open: menuOpen === item.value.groupId }"
                        @mouseenter="resetDropdownTimer"
                        @mouseleave="startDropdownTimer"
                        @scroll.stop>
                    <span class="menu-li"
                          :class="{ disabled: noMoreGroup || item.value.usableStatus === 'PENDING' || isDemo }"
                          @click.stop="duplicateGroupName(item.value)">
                      <i class="icon icon--copy"></i>
                      {{ $t('group.editMode.duplicate') }}
                    </span>
                    <template v-if="item.value.groupId > 1">
                      <span class="menu-li"
                            :class="{ disabled: item.value.usableStatus === 'PENDING' || isDemo }"
                            @click.stop="editGroupName(item.value)">
                        <i class="icon icon--edit"></i>
                        {{ $t('group.editMode.rename') }}
                      </span>
                      <span class="menu-li"
                            :class="{disabled: isDemo}"
                            @click.stop="deleteGroupName(item.value)">
                        <i class="icon icon--delete"></i>
                        {{ $t('group.editMode.delete') }}
                      </span>
                    </template>
                  </span>
                </span>
              </div>
              <div v-else
                   class="input">
                <input type="text"
                       v-model="item.value.groupNameEditing"
                       :id="`editGroupName${item.value.groupId}`"
                       :placeholder="$t('common.placeHolder.within', { count: 10 })"
                       :class="{ error: editingError }"
                       @click.stop
                       @blur="editGroup(item.value, true)"
                       @keyup="setEditError(item.value)"
                       @keyup.esc="editGroup(item.value)"
                       @keyup.enter="$event.target.blur()" />
                <i v-if="!editingError"
                   class="icon icon--close14"
                   @click.stop="clearGroup(item.value, 'clear')"></i>
                <ui-icon-tooltip v-if="editingError"
                                 :iconSize="'red'"
                                 arrowBottom
                                 arrowCenter>
                  <span v-if="editingError === 'duplicate'"
                        v-html="$t('group.editMode.duplicatedGroupName')"></span>
                  <span v-else-if="editingError === 'over'"
                        v-html="$t('group.editMode.withinGroupName', { count: 10 })"></span>
                </ui-icon-tooltip>
              </div>
            </li>
          </ul>
          <button class="add-group"
                  :disabled="noMoreGroup || isDemo"
                  @click="addGroupName">
            <ui-icon-tooltip v-if="noMoreGroup"
                             :iconSize="'yellow'"
                             arrowBottom
                             arrowCenter>
              <span v-html="$t('group.editMode.max3Group')"></span>
            </ui-icon-tooltip>
            {{ $t('group.editMode.addGroup') }}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
              <g fill-rule="evenodd" transform="translate(2 2)">
                <rect width="12" height="2" y="5" rx="1" />
                <rect width="12" height="2" y="5" rx="1" transform="rotate(90 6 6)" />
              </g>
            </svg>
          </button>
        </div>
      </div>

      <ui-icon-tooltip v-if="group && group.groupId === 1"
                       class="automatic-description"
                       :iconSize="'outline-white'"
                       arrowTop
                       arrowCenter>
        <h6 v-html="$t('group.editMode.automaticGuide.title')"></h6>
        <span v-html="$t('group.editMode.automaticGuide.description')"></span>
      </ui-icon-tooltip>

      <div class="count"
           v-if="group"
           :class="{ searching: searchOption && searchOption.text }">
        {{ $t('common.unit.tag') }} : <em>{{ group.usableStatus !== 'PENDING' ? count.tag : 0 | thousand }}</em>
        <span class="gap"></span>
        {{ $t('common.unit.page') }} : <em>{{ group.usableStatus !== 'PENDING' ? count.page : 0 | thousand }}</em>
      </div>
    </div>
    <div v-if="group"
         class="edit-list">
      <template v-if="group.usableStatus !== 'PENDING'">
        <tag-list class="tag-list"
                  :noResult="noResult"
                  :group="group"
                  :active="tag"
                  :searchParam="searchOption"
                  :count="count"
                  :colorList="colorList"
                  :urlsPerPage="urlsPerPage"
                  @noResult="noResult = $event"
                  @searchCount="searchCount = $event"
                  @onTagChange="onTagChange">
        </tag-list><page-list class="page-list"
                              :noResult="noResult"
                              :group="group"
                              :tag="tag"
                              :searchParam="searchOption"
                              :searchCount="searchCount"
                              :count="count"
                              :colorList="colorList"
                              @setUrlsPerPage="urlsPerPage = $event"
                              @noResult="noResult = $event"
                              @onSearch="onSearch" />
      </template>
      <template v-else>
        <div class="duplicating">
          <div class="making">
            <div class="character">
              <i class="icon loading--making"></i>
            </div>
            <div class="group-name">
              <span class="from">{{ group.srcGroupName === 'AUTO' ? $t('group.automatic') : group.srcGroupName }}</span>
              <i class="icon icon--arrow-body-right-gray"></i>
              <span class="to">{{ group.groupName }}</span>
            </div>
            <div class="counts">
              <div class="loading--wrap">
                <i class='loading-bar'></i>
                <i class='loading-bar'></i>
                <i class='loading-bar'></i>
                <i class='loading-bar'></i>
                <i class='loading-bar'></i>
              </div>
              {{ $t('group.editMode.duplicating', { count: thousand(count.page), tail: count.page > 1 ? 's' : '' }) }}
            </div>
          </div>
        </div>
      </template>
      <div v-if="noResult"
           class="no-result">
        <div class="no-data">
          <i class="icon loading--empty"></i>
          <p class="no-data-title">
            {{ $t('common.noResult') }}
          </p>
          <template v-if="searchOption && searchOption.text">
            <p class="no-data-description">
              {{ $t('common.noResultSearch') }}
            </p>
            <p class="no-data-keyword">
              '{{ searchOption.text }}'
            </p>
          </template>
        </div>
      </div>
    </div>

    <dimmed v-if="alerting">
      <group-input v-if="alerting === 'addGroup'"
                   :title="$t('group.editMode.addGroup')"
                   :groupItems="groupItems"
                   @onSubmit="addGroup"
                   @onCancel="cancelAlert" />

      <group-input v-if="alerting === 'duplicateGroup'"
                   :title="$t('group.editMode.duplicateGroup')"
                   :groupItems="groupItems"
                   @onSubmit="duplicateGroup"
                   @onCancel="cancelAlert" />

      <alert v-if="alerting === 'deleteGroup'"
             :icon="'icon--trash-large'"
             :title="$t('group.editMode.confirmDeleteGroup.title')"
             :description="$t('group.editMode.confirmDeleteGroup.description', { name: groupInHand.groupName })"
             :cancel="$t('common.cancel')"
             :submit="$t('common.delete')"
             @onSubmit="deleteGroup"
             @onCancel="cancelAlert" />
    </dimmed>
  </div>
</template>

<script>
import { CommonStorage } from '@/libs/auth-helper'
import { mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { thousand } from '@/libs/utils'

import { getGroupList, getGroupStat, upsertGroup, duplicateGroup, deleteGroup, getPalette } from '@/api/group'

import TagList from '@/components/group/TagList'
import PageList from '@/components/group/PageList'
import GroupInput from '@/components/group/GroupInput'

import Alert from '@/components/ui/Alert'
import Dimmed from '@/components/ui/Dimmed'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle'

export default {
  name: 'GroupEditMode',
  components: {
    TagList,
    PageList,
    GroupInput,
    Alert,
    Dimmed,
    UiIconTooltip,
    UiLoadingCircle
  },
  created () {
    EventBus.$on('onCancelEdit', () => {
      this.onCancel()
    })

    EventBus.$on('reloadGroupStat', () => {
      this.loadGroupStat(true)
    })

    this.setColorList().then(() => {
      this.loadGroupList().then(() => {
        this.onGroupChange(this.groupItems[0].value)
      })
    })
  },
  watch: {
    group () {
      this.onSearch(null)
      this.loadGroupStat()
    },

    searchOption (value, prev) {
      let v = value ? value.term + value.text : value
      let p = prev ? prev.term + prev.text : prev

      if (v !== p) {
        this.onTagChange(null)
      }
    },

    openGroup (value) {
      let bodyListener = (e) => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === 'groupDropdown') {
              outside = false
            }
          }
        }
        if (outside) {
          this.openGroup = false
          this.resetGroupDropdownTimer()
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    }
  },
  methods: {
    loadGroupStat (force = true) {
      EventBus.$emit('onLoading', true)
      return new Promise(resolve => {
        getGroupStat({
          sid: this.sid,
          groupId: this.group.groupId
        }, force).then(result => {
          this.count = {
            tag: result.tags,
            page: result.pages
          }
          EventBus.$emit('onLoading', false)
          resolve()
        })
      })
    },

    loadGroupList (silent = false) {
      if (!silent) {
        EventBus.$emit('onLoading', true)
      }
      return new Promise(resolve => {
        getGroupList({
          sid: this.sid
        }, true).then(result => {
          this.groupItems = []
          for (let group of result) {
            this.groupItems.push({
              text: group.groupName === 'AUTO' ? this.$t('group.automatic') : group.groupName,
              value: {
                ...group,
                groupNameEditing: group.groupName
              }
            })
            if (group.usableStatus === 'PENDING' && !this.pendingTimer) {
              this.checkPending()
            }
          }
          if (!silent) {
            EventBus.$emit('onLoading', false)
          }
          resolve(result)
        })
      })
    },

    onGroupChange (group) {
      this.group = group
      this.tag = null
      this.openGroup = false
    },

    onTagChange (tag) {
      this.tag = tag
    },

    cancelAlert () {
      this.alerting = false
      this.groupInHand = null
    },

    onCancel () {
      this.backToReport()
    },

    onSearch (params = null) {
      this.searchOption = params
    },

    backToReport () {
      const sid = this.$route.query.sid
      const reportViewStorageKey = `reportView-${sid}`
      const storedReportView = CommonStorage.getItem(reportViewStorageKey) || 'ranking'
      const reportView = storedReportView === 'sitemap' ? 'ranking' : storedReportView
      if (storedReportView === 'sitemap') {
        CommonStorage.setItem(reportViewStorageKey, reportView)
      }
      const queryData = {
        sid
      }
      if (this.userData.userId === 'samsung_gmc') {
        queryData.subSid = this.domain.subDomains.find(subDomain => subDomain.domain === 'samsung.com').orgSid
        queryData.path = 'uk'
      }

      this.$router.push({
        name: reportView,
        query: queryData
      })
    },

    checkGroupNameDuplication (groupName) {
      const groupNames = this.groupItems.map(item => item.value.groupName).concat(['자동', 'Automatic', '自動'])
      return groupNames.includes(groupName)
    },

    setColorList () {
      return new Promise(resolve => {
        if (!this.colorList || this.colorList.length < 1) {
          getPalette({
            sid: this.sid
          }).then(result => {
            this.colorList = result
            resolve()
          })
        } else {
          resolve()
        }
      })
    },

    openGroupList () {
      this.openGroup = true
      this.resetGroupDropdownTimer()
    },

    addGroupName () {
      this.alerting = 'addGroup'
    },

    addGroup (groupName) {
      EventBus.$emit('onLoading', true)
      upsertGroup({
        sid: this.sid,
        groupName
      }, true).then(result => {
        this.loadGroupList().then(() => {
          this.onGroupChange(result)
        })
        this.cancelAlert()
        EventBus.$emit('onLoading', false)
      })
    },

    duplicateGroupName (group) {
      if (this.isDemo) return false
      if (!this.noMoreGroup && group.usableStatus !== 'PENDING') {
        this.alerting = 'duplicateGroup'
        this.groupInHand = group
      }
    },

    duplicateGroup (groupName) {
      duplicateGroup({
        sid: this.sid,
        srcGroupId: this.groupInHand.groupId,
        srcGroupName: this.groupInHand.groupName,
        targetGroupName: groupName
      }, true).then(result => {
        result.usableStatus = 'PENDING'
        result.srcGroupName = this.groupInHand.groupName === 'AUTO' ? this.$t('group.automatic') : this.groupInHand.groupName
        this.loadGroupList().then(() => {
          this.onGroupChange(result)
        })
        this.cancelAlert()
      })
    },

    checkPending () {
      this.pendingTimer = setInterval(() => {
        this.loadGroupList(true).then(result => {
          let pending = false
          let refreshNow = false
          let currentOne = null
          for (let group of result) {
            if (group.usableStatus === 'PENDING') {
              pending = true
            }
            if (this.group.groupId === group.groupId) {
              currentOne = group
              if (this.group.usableStatus === 'PENDING' && group.usableStatus !== 'PENDING') {
                refreshNow = true
              }
            }
          }
          if (!pending) {
            clearInterval(this.pendingTimer)
            this.pendingTimer = null
          }
          if (currentOne && refreshNow) {
            this.onGroupChange(currentOne)
          }
        })
      }, 10000)
    },

    setEditError (group) {
      this.editingError = null
      if (group.groupNameEditing && group.groupNameEditing.length > 10) {
        this.editingError = 'over'
      }
    },

    editGroupName (group) {
      if (this.isDemo) return false

      if (!group || group.usableStatus !== 'PENDING') {
        this.editing = group
        this.editingError = null
        this.menuOpen = -1
        if (group) {
          group.groupNameEditing = group.groupName
          setTimeout(() => {
            document.getElementById(`editGroupName${group.groupId}`).focus()
          }, 0)
        }
      }
    },

    editGroup (group, submit = false) {
      setTimeout(() => {
        if (!submit) {
          group.groupNameEditing = group.groupName
        }
        if (this.editingError) {
          submit = false
        }
        if (group) {
          if (group.groupNameEditing === '' || group.groupNameEditing === group.groupName) {
            submit = false
          }
          if (group.groupId !== null) {
            if (this.clearing) {
              group.groupNameEditing = ''
            } else if (!submit) {
              group.groupNameEditing = group.groupName
            } else {
              if (this.editingError) {
                document.getElementById(`editGroupName${group.groupId}`).focus()
                return
              }
              if (this.checkGroupNameDuplication(group.groupNameEditing)) {
                this.editingError = 'duplicate'
                document.getElementById(`editGroupName${group.groupId}`).focus()
                return
              } else {
                group.groupName = group.groupNameEditing
                upsertGroup({
                  sid: this.sid,
                  groupId: group.groupId,
                  groupName: group.groupName
                }, true)
              }
            }
            if (!this.clearing) {
              this.editGroupName(null)
            } else {
              document.getElementById(`editGroupName${group.groupId}`).focus()
              this.clearing = false
            }
          }
        }
      }, 100)
    },

    clearGroup (group, alert) {
      if (alert === 'clear') {
        this.clearing = true
      }
    },

    deleteGroupName (group) {
      if (this.isDemo) return false
      this.alerting = 'deleteGroup'
      this.groupInHand = group
    },

    deleteGroup () {
      EventBus.$emit('onLoading', true)
      deleteGroup({
        sid: this.sid,
        groupId: this.groupInHand.groupId
      }, true).then(() => {
        this.loadGroupList().then(() => {
          if (this.groupInHand.groupId === this.group.groupId) {
            this.onGroupChange(this.groupItems[0].value)
          }
          this.cancelAlert()
        })
        EventBus.$emit('onLoading', false)
      })
    },

    openMenu (id) {
      this.menuOpen = id
      this.startDropdownTimer()
    },

    startGroupDropdownTimer () {
      this.resetGroupDropdownTimer()
      this.groupDropdownTimer = setInterval(() => {
        if (this.groupDropdownTimerTime < 3) {
          this.openGroup = false
        }
        this.groupDropdownTimerTime--
      }, 1000)
    },

    resetGroupDropdownTimer () {
      this.groupDropdownTimerTime = 3
      clearInterval(this.groupDropdownTimer)
      this.groupDropdownTimer = null
    },

    startDropdownTimer () {
      this.resetDropdownTimer()
      this.dropdownTimer = setInterval(() => {
        if (this.dropdownTimerTime < 3) {
          this.menuOpen = -1
        }
        this.dropdownTimerTime--
      }, 1000)
    },

    resetDropdownTimer () {
      this.dropdownTimerTime = 3
      clearInterval(this.dropdownTimer)
      this.dropdownTimer = null
    },

    thousand (value) {
      return thousand(value)
    }
  },
  computed: {
    ...mapState([
      'domain',
      'userData',
      'journeyOptions',
      'isDemo'
    ]),
    sid () {
      return this.$route.query.sid
    },
    noMoreGroup () {
      return this.groupItems.length > 3
    }
  },
  data () {
    return {
      alerting: false,
      groupInHand: null,

      count: {
        tag: '-',
        page: '-'
      },

      group: null,
      tag: null,
      groupItems: [],
      openGroup: false,
      groupDropdownTimer: null,
      groupDropdownTimerTime: null,
      pendingTimer: null,

      searchOption: null,
      searchCount: -1,
      noResult: false,

      editing: null,
      editingError: null,
      clearing: false,
      menuOpen: -1,
      dropdownTimer: null,
      dropdownTimerTime: null,

      colorList: [],

      urlsPerPage: 50
    }
  }
}
</script>

<style lang="scss" scoped>
.group-edit-mode {
  max-width: 1680px;
  min-width: 1280px;
  margin: 0 auto;
  &.shared /deep/{
    .td-tagName,.td-numOfUrl,
    .schedule span {
      pointer-events: none;
      opacity: 0.8
    }
    .add-group {
      pointer-events: none;
      opacity: 0.8;
      filter: grayscale(1)
    }
  }
  .edit-header {
    position: relative;
    z-index: 5;
    height: 30px;
    padding: 21px 30px 0;

    .icon {
      vertical-align: middle;
      margin-top: -1px;
      margin-right: 2px;
    }

    .group-dropdown {
      display: inline-block;
      vertical-align: middle;
      position: relative;

      .selected-group {
        position: relative;
        border-radius: 4px;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.06);
        background-color: #fff;
        margin: 0 8px;
        width: 215px;
        height: 32px;
        line-height: 32px;
        padding: 0 10px;
        color: #2f2f2f;
        cursor: pointer;

        &.disabled {
          color: #888;
        }

        .icon {
          position: absolute;
          top: calc(50% + 1px);
          right: 7px;
          transform: translateY(-50%);
        }

        .pending {
          position: absolute;
          z-index: 9;
          top: calc(50% + 5px);
          left: auto;
          right: 22px;
        }
      }

      .group-list {
        position: absolute;
        top: 0;
        left: 8px;
        z-index: 10;
        width: calc(100% - 16px);
        border-radius: 4px;
        box-shadow: 0 12px 15px -6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.06);
        background-color: #fff;
        transform: scaleY(0);
        transform-origin: top;
        opacity: 0;
        transition: transform 0.3s ease,
                    opacity 0.3s ease;

        &.open {
          transform: scaleY(1);
          opacity: 1;
        }

        li {
          position: relative;
          cursor: pointer;
          text-align: left;
          font-size: 12px;
          font-weight: normal;
          color: #333333;
          line-height: 30px;
          transition: background-color 0.15s ease;
          margin: 2px 0;
          padding: 0 10px;

          .disabled {
            color: #888;
          }

          .input {
            position: relative;

            input {
              padding: 7px 28px 7px 8px;
              border-radius: 4px;
              border: solid 1px #289eef;
              background-color: #fff;
              font-size: 12px;
              line-height: 14px;
              color: #2f2f2f;
              margin: -6px 0 -5px;
              width: 155px;
              max-width: calc(100% - 38px);
              transition: border-color 0.15s ease;

              &.error {
                border-color: #e60724;
              }
            }

            .icon {
              position: absolute;
              top: 8.5px;
              right: 30px;
            }

            & /deep/ .tooltip {
              .icon {
                position: absolute;
                top: 7px;
                right: 7px;
              }

              .bubble {
                bottom: 1px !important;
                margin-left: -16px !important;
                display: block !important;
                opacity: 1 !important;
                padding-bottom: 11px;
              }
            }
          }

          &:hover {
            background-color: #ebebeb;

            .pending {
              right: 27px;
            }

            .tag-menu {
              width: 25px;
              opacity: 1;

              .opener {
                width: 24px;
              }
            }
          }
        }

        .pending {
          position: absolute;
          z-index: 9;
          top: calc(50% + 5px);
          left: auto;
          right: 2px;
          transition: right 0.2s ease;
        }

        .tag-menu {
          position: absolute;
          top: 0;
          right: 10px;
          display: inline-block;
          vertical-align: middle;
          height: 20px;
          margin: -1px 0;
          width: 0;
          opacity: 0;
          transition: width 0.2s ease,
                      opacity 0.2s ease;

          .opener {
            display: inline-block;
            vertical-align: middle;
            margin: -2px 0 0 5px;
            cursor: pointer;
            width: 0;
            height: 24px;
            border-radius: 20px;
            position: relative;
            transition: background-color 0.2s ease,
                        width 0.2s ease;

            .icon {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              margin: 0.5px 0;
            }

            &:hover {
              background-color: rgba(0, 0, 0, 0.1);
            }
          }

          .menu-ul {
            position: absolute;
            z-index: 100;
            left: calc(100% + 7px);
            top: -5px;
            padding: 5px 0;
            border-radius: 4px;
            box-shadow: 0 12px 15px -6px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(0, 0, 0, 0.25);
            background-color: #fff;
            width: 135px;
            transform: scale(0);
            transform-origin: top left;
            transition: transform 0.2s ease;

            &.open {
              transform: scale(1);
            }

            .menu-li {
              cursor: pointer;
              display: block;
              text-align: left;
              font-size: 12px;
              font-weight: normal;
              color: #333333;
              line-height: 30px;
              transition: background-color 0.15s ease;
              margin: 2px 0;

              &.disabled {
                cursor: default;
                opacity: 0.3;
              }

              .icon {
                vertical-align: middle;
                margin: -1px 5px 0 10px;
              }

              &:not(.disabled):hover {
                background-color: #ebebeb;
              }
            }
          }
        }
      }

      .add-group {
        font-size: 13px;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: 38px;
        height: 38px;
        margin-top: 3px;
        letter-spacing: normal;
        color: #ec0047;
        text-align: center;
        width: 100%;
        border-top: 1px solid #ebebeb;
        border-radius: 0 0 4px 4px;
        transition: color 0.15s ease,
                    background-color 0.15s ease;

        &:not(:disabled):hover {
          background-color: #ebebeb;
        }

        &:disabled {
          cursor: default;
          color: #c7c7c7;

          svg {
            g {
              fill: #c7c7c7;
            }
          }
        }

        .tooltip {
          vertical-align: middle;
          margin: 2px 2px -3px 0;

          & /deep/ .bubble {
            margin-bottom: 14px;
            margin-left: -10px !important;
          }
        }

        svg {
          vertical-align: middle;
          margin-top: -4px;

          g {
            fill: #ec0047;
            transition: fill 0.15s ease;
          }
        }
      }
    }

    .automatic-description {
      vertical-align: middle;
      margin-top: 2px;

      /deep/ {
        .bubble {
          margin-top: -1px;
          margin-left: -10px !important;

          .text {
            text-align: left;
            padding: 5px;

            h6 {
              font-size: 13px;
              font-weight: 500;
              line-height: 1.46;
              color: #289eef;
              margin-bottom: 10px;
            }

            span {
              display: block;
              font-size: 13px;
              line-height: 1.46;
              color: #444444;
            }
          }
        }
      }
    }

    .count {
      float: right;
      font-size: 13px;
      line-height: 1;
      color: #757575;
      padding: 18px 11px 0 0;
      transition: opacity 0.3s ease;

      &.searching {
        opacity: 0;
      }

      em {
        color: #444444;
      }

      .gap {
        display: inline-block;
        vertical-align: middle;
        width: 1px;
        height: 8px;
        margin: -2px 9px 0 12px;
        background-color: #d1d1d1;
      }
    }
  }

  .edit-list {
    position: relative;
    height: calc(100vh - 233px);
    min-height: calc(560px - 189px);
    margin: 20px;
    border-radius: 14px;
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.35);
    background-color: #fff;
    padding: 0 10px;

    .no-result {
      position: absolute;
      top: 50px;
      left: 0;
      width: 100%;
      height: calc(100% - 50px);
      background: #ffffff;
      border-radius: 0 0 20px 20px;

      &::before {
        content: "";
        display: block;
        position: absolute;
        top: -50px;
        left: 0;
        width: 60%;
        height: 50px;
        background: #ffffff;
        border-radius: 20px 0 0 0;
      }

      .no-data {
        position: absolute;
        top: calc(50% - 40px);
        left: 50%;
        transform: translate(-50%, -50%);
        text-align: center;

        .no-data-title {
          font-size: 20px;
          font-weight: 700;
          line-height: 1;
          color: #2f2f2f;
          margin: 10px 0 15px;
        }

        .no-data-description {
          font-size: 14px;
          color: #767676;
        }

        .no-data-keyword {
          margin-top: 30px;
          font-size: 15px;
          font-weight: 500;
          color: #ff8400;
        }
      }
    }

    .tag-list {
      display: inline-block;
      vertical-align: top;
      width: 350px;
    }

    .page-list {
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 350px);
    }
  }

  .making {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;

    .group-name {
      span {
        display: inline-block;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 500;
        color: #555;

        &.to {
          font-size: 15px;
        }
      }

      .icon {
        vertical-align: middle;
        margin: -2px 10px 0;
      }
    }

    .counts {
      border-top: 2px solid #d8d8d8;
      width: 300px;
      margin-top: 17px;
      padding: 27px 0 82px;
      font-size: 14px;
      font-weight: 500;
      color: #ec0047;

      .loading--wrap {
        position: relative;
        width: 180px;
        height: 12px;
        margin: 0 0 11px;

        .loading-bar {
          display: block;
          position: absolute;
          padding: 6px;
          border-radius: 50%;
          background-color: $brandColor;
          animation: loading 1.4s ease-in-out 0s infinite;

          &:nth-child(1) {
            animation-delay: 0s;
          }

          &:nth-child(2) {
            animation-delay: 0.15s;
          }

          &:nth-child(3) {
            animation-delay: 0.3s;
          }

          &:nth-child(4) {
            animation-delay: 0.45s;
          }

          &:nth-child(5) {
            animation-delay: 0.6s;
          }
        }
      }
    }
  }

  @keyframes loading {
    0% {
      transform: translateX(-15px);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: translateX(180px);
      opacity: 0;
    }
  }
}
</style>
